// FUNCTIONALITY-102 / MINIAPP-015D / R7.2.2D — single customer-facing money utility.
// Ordinary retail prices (>= 1 UAH): ceil_whole_uah (348.80 → 349).
// Canary / fractional amounts (< 1 UAH, e.g. 0.01) must NOT round up to 1 UAH.

/** Round UP to whole UAH for ordinary catalog prices. Non-finite / non-positive -> 0. */
export function roundUpUah(amount: number | null | undefined): number {
  if (amount == null || !Number.isFinite(amount) || amount <= 0) return 0;
  return Math.ceil(amount - 1e-9);
}

/**
 * R7.2.2D — canary isolation: only amounts strictly below 1 UAH keep decimals.
 * Ordinary fractional retail (348.8) is NOT canary and must ceil for sell/display.
 */
export function isFractionalUah(amount: number | null | undefined): boolean {
  if (amount == null || !Number.isFinite(amount) || amount <= 0) return false;
  return amount < 1;
}

/**
 * Canonical Customer payable / sell price (FUNCTIONALITY-102 ceil_whole_uah).
 * Alias preferred name for R7.2.2D contract; same SoT as roundUpUah for >= 1.
 */
export function getCustomerSalePrice(amount: number | null | undefined): number {
  if (amount == null || !Number.isFinite(amount) || amount <= 0) return 0;
  if (amount < 1) {
    // Canary: keep 2dp major units; never ceil 0.01 → 1.
    return Math.round(amount * 100) / 100;
  }
  return roundUpUah(amount);
}

/**
 * Format UAH for customer UI after applying payable policy.
 * Whole amounts → "349 ₴"
 * Canary (<1) → "0,01 ₴" — never "1 ₴"
 */
export function formatUah(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount) || amount <= 0) return "0 ₴";
  const payable = getCustomerSalePrice(amount);
  if (isFractionalUah(payable) || payable < 1) {
    const fixed = payable.toFixed(2).replace(".", ",");
    return `${fixed} ₴`;
  }
  const whole = Math.trunc(payable);
  const fmt = whole.toLocaleString("uk-UA").replace(/\u00A0/g, " ").replace(/,/g, " ");
  return `${fmt} ₴`;
}

/** Preferred R7.2.2D display alias — same SoT as formatUah. */
export function formatCustomerPriceUAH(amount: number | null | undefined): string {
  return formatUah(amount);
}

/** Convert minor units (kopiiky) to UAH major. 1 minor = 0.01 UAH. */
export function minorToUah(minor: number | null | undefined): number {
  if (minor == null || !Number.isFinite(minor)) return 0;
  return Number(minor) / 100;
}

export function formatMinorAsUah(minor: number | null | undefined): string {
  return formatUah(minorToUah(minor));
}
